import React, { useEffect, useState } from 'react';
import { Edit } from 'lucide-react';
import { validateOIB } from '../../utils/validations';
import { t } from '../../i18n/languages';

const EditUserModal = ({
  show,
  user,
  editUser,
  setEditUser,
  onClose,
  onSubmit,
  currentLanguage,
  yearStatus,
  loading
}) => {
  const [originalYearContract, setOriginalYearContract] = useState('');
  const [errors, setErrors] = useState({});
  const [wasShown, setWasShown] = useState(false);
  // Clear errors and modal data when modal is opened
  useEffect(() => {
    if (show && !wasShown) {
      setErrors({});
      setWasShown(true);
    } else if (!show && wasShown) {
      setWasShown(false); // reset so it works next time
    }
  }, [show, wasShown]);

  useEffect(() => {
    if (user) {
      setOriginalYearContract(user.current_year?.toString() || '');
    }
  }, [user]);

  const handleOIBChange = (e) => {
    const newOIB = e.target.value;
    setEditUser({ ...editUser, oib: newOIB });

    // Clear previous OIB error when user starts typing
    if (errors.oib) {
      setErrors({ ...errors, oib: '' });
    }

    // Validate OIB in real-time if it has 11 digits
    if (newOIB.length === 11) {
      const validation = validateOIB(newOIB, currentLanguage);
      if (!validation.isValid) {
        setErrors({ ...errors, oib: validation.message });
      }
    }
  };
  
  const handleVacationChange = (e) => {
    const newVacation = e.target.value;
    setEditUser({ ...editUser, vacation_contract: newVacation });

    // Clear previous vacation error when user starts typing
    if (errors.vacation_contract) {
      setErrors({ ...errors, vacation_contract: '' });
    }

    // Validate vacation in real-time if it has 11 digits
    if (newVacation.length === 11) {
      const validation = validateOIB(newVacation, currentLanguage);
      if (!validation.isValid) {
        setErrors({ ...errors, vacation_contract: validation.message });
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate all fields
    const newErrors = {};

    // Validate OIB
    if (editUser.oib) {
      const oibValidation = validateOIB(editUser.oib, currentLanguage);
      if (!oibValidation.isValid) {
        newErrors.oib = oibValidation.message;
      }
    }

    // Add other validations here if needed (email, username, etc.)
    if (!editUser.username?.trim()) {
      newErrors.username = t('userNameRequired', {}, currentLanguage);
    }
    if (!editUser.email?.trim()) {
      newErrors.email = t('emailRequired', {}, currentLanguage);
    }
    if (!editUser.first_name?.trim()) {
      newErrors.first_name = t('firstNameRequired', {}, currentLanguage);
    }
    if (!editUser.last_name?.trim()) {
      newErrors.last_name = t('lastNameRequired', {}, currentLanguage);
    }
    if (!editUser.oib?.trim()) {
      newErrors.oib = t('oibRequired', {}, currentLanguage);
    }
    if (editUser.vacation_contract <= 0) {
      newErrors.vacation_contract = t('vacationContractRequired', {}, currentLanguage);
    }

    setErrors(newErrors);

    // If there are validation errors, don't submit
    if (Object.keys(newErrors).length > 0) {
      return;
    }

    const current = editUser.current_year_contract?.toString() || '';
    const hasYearContractChanged = current !== originalYearContract;
    onSubmit(hasYearContractChanged);
  };

  if (!show || !user) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '24px',
        maxWidth: '500px',
        width: '90%',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            backgroundColor: '#eff6ff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Edit size={24} color="#3b82f6" />
          </div>
          <h3 style={{
            margin: 0,
            fontSize: '20px',
            fontWeight: '600',
            color: '#1f2937'
          }}>
            {t('editUser', {}, currentLanguage)}
          </h3>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Username and Email */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
            <InputField label={t('username', {}, currentLanguage)}
              value={editUser.username}
              onChange={(e) => setEditUser({ ...editUser, username: e.target.value })}
              error={errors.username} />
            <InputField label="Email"
              type="email"
              value={editUser.email}
              onChange={(e) => setEditUser({ ...editUser, email: e.target.value })}
              error={errors.email} />
          </div>

          {/* Name, Surname, Role, Vacation Contract */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '20px' }}>
            <InputField label={t('firstName', {}, currentLanguage)}
              value={editUser.first_name}
              onChange={(e) => setEditUser({ ...editUser, first_name: e.target.value })}
              error={errors.first_name} />
            <InputField label={t('lastName', {}, currentLanguage)}
              value={editUser.last_name}
              onChange={(e) => setEditUser({ ...editUser, last_name: e.target.value })}
              error={errors.last_name} />
            <div>
              <label style={labelStyle}>{t('role', {}, currentLanguage)}</label>
              <select
                value={editUser.role}
                onChange={(e) => setEditUser({ ...editUser, role: e.target.value })}
                style={inputStyle}>
                <option value="employee">{t('employee', {}, currentLanguage)}</option>
                <option value="admin">{t('admin', {}, currentLanguage)}</option>
              </select>
            </div>
            <InputField label={t('vacationDays', {}, currentLanguage)}
              type="number"
              min="0"
              max="50"
              value={editUser.vacation_contract}
              onChange={(e) => handleVacationChange(e)}
              error={errors.vacation_contract} />
          </div>

          {/* Current Year Vacation + Start Date */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
            <InputField
              label={currentLanguage === 'hr' ? `G.O. za tekuću godinu (${yearStatus?.currentOpenYear || new Date().getFullYear()})` : `Vacation Days for Current Year (${yearStatus?.currentOpenYear || new Date().getFullYear()})`}
              type="number"
              value={editUser.current_year_contract}
              onChange={(e) => setEditUser({ ...editUser, current_year_contract: e.target.value })}
            />
            <InputField
              label={t('employmentStartDate', {}, currentLanguage)}
              type="date"
              value={editUser.employment_start_date}
              onChange={(e) => setEditUser({ ...editUser, employment_start_date: e.target.value })}
            />
          </div>

          {/* OIB + Employment Type */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
            <InputField label={'OIB'}
              type="number"
              value={editUser.oib}
              onChange={handleOIBChange}
              error={errors.oib} />
            <div>
              <label style={labelStyle}>{t('employmentType', {}, currentLanguage)}</label>
              <select
                value={editUser.employment_type}
                onChange={(e) => setEditUser({ ...editUser, employment_type: e.target.value })}
                style={inputStyle}>
                <option value="full-time">{t('fullTime', {}, currentLanguage)}</option>
                <option value="part-time">{t('partTime', {}, currentLanguage)}</option>
              </select>
            </div>
          </div>

          {/* Buttons */}
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              style={cancelBtnStyle}
              onMouseOver={(e) => {
                e.target.style.backgroundColor = '#f3f4f6';
                e.target.style.color = '#374151';
              }}
              onMouseOut={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = '#6b7280';
              }}
            >
              {t('cancel', {}, currentLanguage)}
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                ...submitBtnStyle,
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1
              }}
              onMouseOver={(e) => !loading && (e.target.style.backgroundColor = '#2563eb')}
              onMouseOut={(e) => !loading && (e.target.style.backgroundColor = '#3b82f6')}
            >
              {loading ? t('editing', {}, currentLanguage) : t('editUser', {}, currentLanguage)}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const InputField = ({ label, value, onChange, type = "text", error, ...props }) => (
  <div>
    <label style={labelStyle}>{label}</label>
    <input
      type={type}
      value={value}
      onChange={onChange}
      style={inputStyle}
      {...props}
    />
    {error && <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>{error}</div>}
  </div>
);

const labelStyle = {
  display: 'block',
  fontSize: '14px',
  fontWeight: '500',
  color: '#374151',
  marginBottom: '6px'
};

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid #d1d5db',
  borderRadius: '6px',
  fontSize: '14px'
};

const cancelBtnStyle = {
  padding: '10px 20px',
  backgroundColor: 'transparent',
  color: '#6b7280',
  border: '1px solid #d1d5db',
  borderRadius: '8px',
  fontSize: '14px',
  fontWeight: '500',
  cursor: 'pointer',
  transition: 'all 0.2s'
};

const submitBtnStyle = {
  padding: '10px 20px',
  backgroundColor: '#3b82f6',
  color: 'white',
  border: 'none',
  borderRadius: '8px',
  fontSize: '14px',
  fontWeight: '500',
  transition: 'background-color 0.2s'
};

export default EditUserModal;
